import React, { useState, useEffect, useCallback, useRef } from "react";
import GoogleMapReact from "google-map-react";
import Button from "./HexButton/button";
import styled from "styled-components";
import AutoComplete from "./Autocomplete";
import Marker from "./Marker";
import "./auto.css";
import apikeymap from "../../../../apikey";

const Wrapper = styled.main`
  width: 100%;
  height: 90vh;
  position: relative;
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: -75px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e74c3c;
  border-top: 4px solid transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CurrentLocationBtn = styled.button`
  position: absolute;
  bottom: 100px;
  right: 12px;
  z-index: 10;
  background: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;

  &:hover {
    background: #f0f0f0;
  }
`;

const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };

const MyGoogleMap = ({ latlag, SelectedLocation }) => {
  const getEditCenter = () => {
    if (SelectedLocation?.lat && SelectedLocation?.lng) {
      return {
        lat: parseFloat(SelectedLocation.lat),
        lng: parseFloat(SelectedLocation.lng),
      };
    }
    return INDIA_CENTER;
  };

  const initialCenter = getEditCenter();
  const isInitialEditMode = !!(SelectedLocation?.lat && SelectedLocation?.lng);

  const [mapApiLoaded, setMapApiLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapApi, setMapApi] = useState(null);

  const [center, setCenter] = useState(initialCenter);
  const [zoom, setZoom] = useState(isInitialEditMode ? 18 : 5);
  const [lat, setLat] = useState(initialCenter.lat);
  const [lng, setLng] = useState(initialCenter.lng);

  const [locationName, setLocationName] = useState(
    isInitialEditMode ? "Fetching selected location..." : "Fetching your location..."
  );

  const [isDragging, setIsDragging] = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  const debounceRef = useRef(null);

  // Flag to distinguish a programmatic panTo/setZoom (which we already
  // geocoded for) from a real user drag/zoom on the map. Prevents onChange
  // from firing a duplicate, wasted Geocoding API call.
  const isProgrammaticMove = useRef(false);

  // Tracks the last SelectedLocation (lat,lng) we've already moved to /
  // geocoded, so the effect below doesn't repeat the same work when
  // mapInstance/mapApi change (e.g. right after apiHasLoaded sets them).
  const lastHandledLocationRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const getLocationName = useCallback(
    (latitude, longitude, mapsApi) => {
      const api = mapsApi || mapApi;
      if (!api) return;

      const geocoder = new api.Geocoder();

      geocoder.geocode(
        { location: { lat: latitude, lng: longitude } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const locality = results.find(
              (r) =>
                r.types.includes("locality") ||
                r.types.includes("sublocality") ||
                r.types.includes("administrative_area_level_2")
            );

            setLocationName((locality || results[0]).formatted_address);
          } else {
            setLocationName(`Error: ${status}`);
          }
        }
      );
    },
    [mapApi]
  );

  // Pans/zooms the map programmatically while flagging the move so the
  // resulting onChange event doesn't trigger a second geocode call.
  const moveMapTo = useCallback((map, newCenter, newZoom = 18) => {
    if (!map) return;
    isProgrammaticMove.current = true;
    map.panTo(newCenter);
    map.setZoom(newZoom);
  }, []);

  // Single source of truth for reacting to SelectedLocation: handles both
  // the initial edit-mode center (once the map is ready) and any later
  // change to SelectedLocation. Guarded by lastHandledLocationRef so it
  // never repeats the same lat/lng — this is what previously fired twice
  // (once from apiHasLoaded, once from this effect re-running when
  // mapInstance/mapApi flipped from null to set) and doubled the
  // Geocoding calls.
  useEffect(() => {
    if (!SelectedLocation?.lat || !SelectedLocation?.lng) return;
    if (!mapInstance || !mapApi) return; // wait until the map is ready

    const newCenter = {
      lat: parseFloat(SelectedLocation.lat),
      lng: parseFloat(SelectedLocation.lng),
    };

    if (Number.isNaN(newCenter.lat) || Number.isNaN(newCenter.lng)) return;

    const key = `${newCenter.lat},${newCenter.lng}`;
    if (lastHandledLocationRef.current === key) return; // already handled
    lastHandledLocationRef.current = key;

    setCenter(newCenter);
    setLat(newCenter.lat);
    setLng(newCenter.lng);
    setZoom(18);
    setLocationLoading(false);

    moveMapTo(mapInstance, newCenter, 18);
    getLocationName(newCenter.lat, newCenter.lng, mapApi);
  }, [
    SelectedLocation?.lat,
    SelectedLocation?.lng,
    mapInstance,
    mapApi,
    getLocationName,
    moveMapTo,
  ]);

  const apiHasLoaded = useCallback(
    (map, maps) => {
      setMapApiLoaded(true);
      setMapInstance(map);
      setMapApi(maps);

      // Edit-mode centering/geocoding is handled by the SelectedLocation
      // effect above once mapInstance/mapApi are set — don't duplicate it
      // here.
      if (SelectedLocation?.lat && SelectedLocation?.lng) return;

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            const newCenter = {
              lat: coords.latitude,
              lng: coords.longitude,
            };

            setCenter(newCenter);
            setLat(newCenter.lat);
            setLng(newCenter.lng);
            setZoom(18);
            setLocationLoading(false);

            moveMapTo(map, newCenter, 18);

            getLocationName(newCenter.lat, newCenter.lng, maps);
          },
          () => {
            setLocationName("Enable location for better experience");
            setLocationLoading(false);
            getLocationName(INDIA_CENTER.lat, INDIA_CENTER.lng, maps);
          },
          {
            timeout: 10000,
            maximumAge: 60000,
            enableHighAccuracy: true,
          }
        );
      } else {
        setLocationLoading(false);
        getLocationName(INDIA_CENTER.lat, INDIA_CENTER.lng, maps);
      }
    },
    [SelectedLocation?.lat, SelectedLocation?.lng, getLocationName, moveMapTo]
  );

  const fetchCurrentLocation = useCallback(() => {
    if (!mapInstance || !mapApi) return;

    if (!("geolocation" in navigator)) {
      setLocationName("Geolocation supported nahi hai");
      return;
    }

    setFetchingLocation(true);
    setLocationName("Fetching Location...");

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const newCenter = {
          lat: coords.latitude,
          lng: coords.longitude,
        };

        setCenter(newCenter);
        setLat(newCenter.lat);
        setLng(newCenter.lng);
        setZoom(18);
        setFetchingLocation(false);
        setLocationLoading(false);

        moveMapTo(mapInstance, newCenter, 18);

        getLocationName(newCenter.lat, newCenter.lng);
      },
      (error) => {
        console.warn("Geolocation denied:", error.message);
        setLocationName("Location access denied — manually select karo");
        setFetchingLocation(false);
      },
      {
        timeout: 10000,
        maximumAge: 0,
        enableHighAccuracy: true,
      }
    );
  }, [mapInstance, mapApi, getLocationName, moveMapTo]);

  const onChange = useCallback(
    ({ center, zoom }) => {
      if (isDragging) return;

      setCenter(center);
      setZoom(zoom);
      setLat(center.lat);
      setLng(center.lng);

      // This onChange cycle was caused by our own panTo()/setZoom() call,
      // for which we've already fired (or are about to fire) a direct
      // getLocationName() call. Consume the flag and skip the duplicate
      // debounced geocode.
      if (isProgrammaticMove.current) {
        isProgrammaticMove.current = false;
        return;
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        getLocationName(center.lat, center.lng);
      }, 600);
    },
    [getLocationName, isDragging]
  );

  const addPlace = useCallback(
    (place) => {
      if (!place?.geometry?.location) return;

      const newLat = place.geometry.location.lat();
      const newLng = place.geometry.location.lng();

      const newCenter = {
        lat: newLat,
        lng: newLng,
      };

      setCenter(newCenter);
      setLat(newLat);
      setLng(newLng);
      setZoom(18);
      setLocationLoading(false);
      setLocationName(place.formatted_address || place.name || "Selected location");

      if (mapInstance) {
        moveMapTo(mapInstance, newCenter, 18);
      }
    },
    [mapInstance, moveMapTo]
  );

  const onMarkerDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onMarkerDrag = useCallback((e) => {
    setLat(e.lat);
    setLng(e.lng);
  }, []);

  const onMarkerDragEnd = useCallback(
    (e) => {
      setIsDragging(false);

      const newCenter = {
        lat: e.lat,
        lng: e.lng,
      };

      setLat(e.lat);
      setLng(e.lng);
      setCenter(newCenter);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        getLocationName(e.lat, e.lng);
      }, 300);
    },
    [getLocationName]
  );

  const handleChange = useCallback(() => {
    if (latlag && lat && lng) {
      latlag(lat, lng, locationName);
    }
  }, [lat, lng, locationName, latlag]);

  return (
    <>
      {mapApiLoaded && (
        <div>
          <AutoComplete map={mapInstance} mapApi={mapApi} addplace={addPlace} />

          <Button buttonTitle="Select" id="mapbutton" onClick={handleChange} />
        </div>
      )}

      <Wrapper>
        <GoogleMapReact
          center={center}
          zoom={zoom}
          onChange={onChange}
          bootstrapURLKeys={{
            key: apikeymap?.apikey,
            libraries: ["places", "geometry"],
          }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => apiHasLoaded(map, maps)}
        >
          {locationLoading ? (
            <div
              lat={INDIA_CENTER.lat}
              lng={INDIA_CENTER.lng}
              style={{ transform: "translate(-50%, -50%)" }}
            >
              <Spinner />
            </div>
          ) : (
            <Marker
              lat={lat}
              lng={lng}
              text={locationName}
              draggable={true}
              onDragStart={onMarkerDragStart}
              onDrag={onMarkerDrag}
              onDragEnd={onMarkerDragEnd}
              isDragging={isDragging}
            />
          )}
        </GoogleMapReact>

        {mapApiLoaded && (
          <CurrentLocationBtn onClick={fetchCurrentLocation} title="Current location">
            {fetchingLocation ? "⏳" : "📍"}
          </CurrentLocationBtn>
        )}
      </Wrapper>
    </>
  );
};

export default MyGoogleMap;